import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { epApi, showError } from '../api';
import { mdy, todayIso } from '../format';
import { PlanView, TaskView } from '../types';

/**
 * "Set a deadline" for a tracked program that has not posted one. The checklist counts
 * back from it and it shows as not official. The program's own deadline replaces it
 * when the program posts. Student and counselor can set it; a parent only sees it.
 */
export default function DeadlineOverride({ studentId, programId, override, canSet, viewer, studentName, onSaved }: {
  studentId: number;
  programId: number;
  override: PlanView['deadline_override'];
  canSet: boolean;
  viewer: 'student' | 'counselor' | 'parent';
  studentName?: string;
  onSaved: (pv: PlanView) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(override?.date ?? '');
  const [busy, setBusy] = useState(false);
  const who = (by: string | null) =>
    by === 'counselor' ? (viewer === 'counselor' ? 'you' : 'the counselor') : by === 'student' ? (viewer === 'student' ? 'you' : studentName || 'the student') : 'GradMap';
  const save = async (d: string | null) => {
    setBusy(true);
    try {
      const pv = await epApi.setDeadlineOverride(studentId, programId, d);
      onSaved(pv);
      setEditing(false);
      toast.success(d ? 'Deadline set. The checklist counts back from it.' : 'Deadline removed.');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="ep-ovr">
        <label className="small" htmlFor={`ovr-${programId}`} style={{ fontWeight: 500 }}>Deadline to use until the program posts one</label>
        <div className="row" style={{ marginTop: 6 }}>
          <input id={`ovr-${programId}`} type="date" className="in" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} style={{ maxWidth: 180 }} />
          <button type="button" className="btn sm primary" disabled={busy || !date} onClick={() => save(date)}>Save</button>
          <button type="button" className="btn sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
        </div>
        <p className="muted small" style={{ marginTop: 6 }}>Shown as not official. When the program posts its deadline, that date replaces this one.</p>
      </div>
    );
  }
  if (override) {
    return (
      <div className="ep-ovr">
        <div className="row" style={{ gap: 8 }}>
          <span className="chip a" title="Not posted by the program">Not official</span>
          <span className="small">{`Deadline ${mdy(override.date)}, set by ${who(override.by)}`}</span>
        </div>
        {canSet && (
          <div className="row" style={{ marginTop: 6 }}>
            <button type="button" className="linkish small" onClick={() => { setDate(override.date); setEditing(true); }}>Change</button>
            <button type="button" className="linkish small" disabled={busy} onClick={() => save(null)}>Remove</button>
          </div>
        )}
      </div>
    );
  }
  if (!canSet) return null;
  return (
    <div className="ep-ovr">
      <button type="button" className="btn sm" onClick={() => setEditing(true)}>Set a deadline</button>
      <p className="muted small" style={{ marginTop: 6 }}>No deadline posted yet. Set one to plan the steps; it shows as not official.</p>
    </div>
  );
}

/**
 * The Deadline field inside a task (All tasks panel, program page task). A program
 * deadline shows as set by the program. While the program has none, the student or
 * counselor can set one for the whole program from here; it shows as not official.
 */
export function DeadlineField({ task, studentId, canSet, onChanged, className = 'at-ro', programDeadline = null }: {
  task: TaskView;
  studentId: number;
  canSet: boolean;
  /** The deadline the program has posted, if any. Then nothing can be set here. */
  programDeadline?: string | null;
  onChanged: (pv: PlanView) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(task.deadline_source === 'override' ? task.official_deadline ?? '' : '');
  const [busy, setBusy] = useState(false);
  // The program-wide date just saved here, for a step that has no deadline of its own.
  const [programDate, setProgramDate] = useState<string | null>(null);
  const program = task.origin === 'program_requirement' && !!task.program_id;
  const save = async (d: string | null) => {
    if (!task.program_id) return;
    setBusy(true);
    try {
      const pv = await epApi.setDeadlineOverride(studentId, task.program_id, d);
      setProgramDate(pv.deadline_override?.date ?? null);
      onChanged(pv);
      setEditing(false);
      toast.success(d ? 'Deadline set for this program. Its steps count back from it.' : 'Deadline removed.');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  if (editing) {
    return (
      <div>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input type="date" className="in" aria-label="Deadline for this program" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
          <button type="button" className="btn sm primary" disabled={busy || !date} onClick={() => save(date)}>Save</button>
          <button type="button" className="btn sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
        </div>
        <p className="muted small" style={{ marginTop: 4 }}>Used for the whole program until it posts its own. Shown as not official.</p>
      </div>
    );
  }
  if (task.official_deadline && task.deadline_source === 'override') {
    return (
      <div className={className}>
        {mdy(task.official_deadline)}
        <small>Not official. Set until the program posts its deadline.</small>
        {canSet && program && (
          <span className="row" style={{ gap: 12, marginTop: 4 }}>
            <button type="button" className="linkish small" onClick={() => setEditing(true)}>Change</button>
            <button type="button" className="linkish small" disabled={busy} onClick={() => save(null)}>Remove</button>
          </span>
        )}
      </div>
    );
  }
  if (task.official_deadline) {
    return <div className={`${className} red`}>{mdy(task.official_deadline)}<small>Set by the program</small></div>;
  }
  if (programDeadline && !task.official_deadline && program) {
    return (
      <div className={className}>
        No deadline on this step
        <small>{`The program's deadline is ${mdy(programDeadline)}.`}</small>
      </div>
    );
  }
  if (programDate) {
    return (
      <div className={className}>
        {`Program deadline ${mdy(programDate)}`}
        <small>Not official. This step has no deadline of its own; its target date counts back from it.</small>
      </div>
    );
  }
  return (
    <div className={className}>
      {program ? 'Not posted yet' : 'None'}
      {program && <small>GradMap adds it when the program posts it</small>}
      {canSet && program && (
        <button type="button" className="linkish small" style={{ marginTop: 4, textAlign: 'left' }} onClick={() => setEditing(true)}>Set a deadline</button>
      )}
    </div>
  );
}
